import InspectionPointItem from '@components/ProjectOverview/Inspections/InspectionPointItem';
import {
  EmptyPOIContainer, InfoChip, InspectionIconTile, SnapshotChip,
} from '@components/ProjectOverview/Inspections/Inspections.styled';
import InspectionStatusSummary from '@components/ProjectOverview/Inspections/InspectionStatusSummary';
import { useInspectionSnapshots } from '@components/ProjectOverview/Inspections/useInspectionSnapshots';
import ImageGalleryModal from '@components/Shared/ImageGalleryModal';
import { InspectionReportDownload } from '@components/Shared/Inspection/InspectionReportDownload';
import Header from '@components/Shared/Layout/Header';
import InspectionDetailModal from '@components/Viewer/UserInterface/InspectionDetail/InspectionDetail';
import { useLocalization } from '@lib/localization';
import { buildProjectPath, VIEWER_BASE } from '@lib/routing/projectPath';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import SPInspection from '@lib/spproject/SPInspection';
import { type Inspection } from '@lib/spproject/types';
import { OpenInBrowser } from '@mui/icons-material';
import { Box, IconButton, Pagination, Paper, Stack, Tooltip, Typography } from '@mui/material';
import {
  useGetInspectionQuery, useLazyGetInspectionFileQuery, type InspectionFields,
} from '@store/api/inspectionEndpoints';
import { folderPathOf, useGetProjectQuery } from '@store/api/projectEndpoints';
import { useAppDispatch, useLoading } from '@store/hooks';
import { setInspection, setSelectedPOI, setStepIndex } from '@store/reducers/viewerSlice';
import { Calendar, ClipboardCheck, Images, User } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Both spellings of the two renamed fields. `notes` and `documents` are what older
 * inspections are stored under, so asking for only the new names returns neither.
 */
const INSPECTION_FIELDS: InspectionFields = ['notes', 'pointOfInterests', 'documents', 'resources'];

const POIS_PER_PAGE = 10;

/**
 * One inspection: its points of interest, its snapshots, its report.
 *
 * A page rather than a fold inside the list, so the URL that already addressed an
 * inspection (`…/{projectId}/inspections/{inspectionId}`) opens it directly instead of
 * depending on the list having loaded and the right row having been expanded. The list
 * stays a list: it downloads no inspection files at all now.
 */
const InspectionDetailPage = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { show, hide } = useLoading();
  const componentId = useId();

  const { projectId, folderPath, inspectionId, poiId, stepIndex, buildPath } = useProjectRoute();

  const { data: project } = useGetProjectQuery({ id: projectId ?? '' }, { skip: !projectId });

  const { data: inspection, isLoading } = useGetInspectionQuery(
    { projectId: projectId ?? '', inspectionId: inspectionId ?? '', include: INSPECTION_FIELDS },
    { skip: !projectId || !inspectionId },
  );

  const [fetchInspectionFile] = useLazyGetInspectionFileQuery();

  // One inspection per page, so one slot: the parsed file is what the POI overlay and the
  // snapshot gallery both need, and parsing it twice is the cost this avoids.
  const parsed = useRef<SPInspection | null>(null);

  // Which inspection the viewer slice is already holding. `setInspection` closes the POI
  // overlay, so sending it again while the overlay is open — which moving from one point to
  // the next would do — shuts it and reopens it in front of the reader.
  const inspectionInStore = useRef<string | null>(null);

  const [poiLoading, setPoiLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const loadParsed = useCallback(async (id: string): Promise<SPInspection> => {
    if (parsed.current) return parsed.current;

    const file = await fetchInspectionFile({ projectId: projectId ?? '', inspectionId: id }).unwrap();
    const loaded = await SPInspection.loadAsync(file);

    parsed.current = loaded;

    return loaded;
  }, [fetchInspectionFile, projectId]);

  const { snapshots, loading: snapshotsLoading, load: loadSnapshots } = useInspectionSnapshots(
    inspectionId ?? '', loadParsed,
  );

  const pois = useMemo<Inspection.PointOfInterest[]>(
    () => inspection?.pointOfInterests ?? [], [inspection?.pointOfInterests],
  );

  const snapshotCount = useMemo(
    () => (inspection?.resources ?? []).filter(SPInspection.isSnapshot).length, [inspection?.resources],
  );

  const snapshotImages = useMemo(
    () => snapshots.map((snapshot) => ({
      original: snapshot.resourceUrl,
      thumbnail: snapshot.resourceUrl,
      description: snapshot.name,
    })),
    [snapshots],
  );

  const pagedPois = useMemo(() => {
    const start = (page - 1) * POIS_PER_PAGE;

    return { data: pois.slice(start, start + POIS_PER_PAGE), totalPages: Math.ceil(pois.length / POIS_PER_PAGE) };
  }, [page, pois]);

  // The folder prefix is context, not authority — the same correction the project page
  // makes, so a link minted before the project moved still tidies itself up.
  useEffect(() => {
    if (!project || !projectId || !inspectionId) return;

    const actualFolderPath = folderPathOf(project);

    if (actualFolderPath === folderPath) return;

    navigate(
      buildProjectPath({ folderPath: actualFolderPath, projectId, inspectionId, poiId, stepIndex }),
      { replace: true },
    );
  }, [folderPath, inspectionId, navigate, poiId, project, projectId, stepIndex]);

  // A POI in the URL opens the overlay on arrival: the page is reachable by link, so it
  // cannot rely on having been clicked into.
  useEffect(() => {
    if (!inspectionId) return;

    // The URL decides whether the overlay is up. Closing it navigates here without the
    // point, and this page stays mounted through that — nothing else would clear the
    // selection the overlay reads, so it would close and immediately draw itself again.
    if (!poiId) {
      dispatch(setSelectedPOI(null));
      return;
    }

    let cancelled = false;

    const openPoi = async () => {
      setPoiLoading(true);

      try {
        const loaded = await loadParsed(inspectionId);

        if (cancelled) return;

        if (inspectionInStore.current !== inspectionId) {
          dispatch(setInspection(loaded));
          inspectionInStore.current = inspectionId;
        }

        const poi = loaded.inspection.pointOfInterests.find((candidate) => candidate.id === poiId);

        if (!poi) throw new Error('POI not found');

        dispatch(setSelectedPOI(poi));

        const index = Number(stepIndex);

        dispatch(setStepIndex(Number.isNaN(index) ? 0 : index));
      } catch (error) {
        console.error('Failed to load POI:', { inspectionId, poiId, error });
        dispatch(setInspection(null));
      } finally {
        if (!cancelled) setPoiLoading(false);
      }
    };

    void openPoi();

    return () => { cancelled = true; };
  }, [dispatch, inspectionId, loadParsed, poiId, stepIndex]);

  /**
   * The viewer slice is shared with the 3D viewer, so a selection left in it would surface
   * there. Cleared on the way out only — clearing it per change would race the effect above,
   * which sets it on the way in.
   */
  useEffect(() => () => {
    inspectionInStore.current = null;
    dispatch(setInspection(null));
    dispatch(setSelectedPOI(null));
    dispatch(setStepIndex(0));
  }, [dispatch]);

  useEffect(() => {
    (isLoading || poiLoading ? show : hide)(componentId);
  }, [componentId, hide, isLoading, poiLoading, show]);

  /**
   * The jump the project page offers, carrying the inspection with it: the viewer is the
   * same URL grammar under its own base, so this costs no lookup. A blocked popup falls
   * back to navigating in place rather than doing nothing at all.
   */
  const openInViewer = () => {
    const route = buildProjectPath({
      base: VIEWER_BASE, folderPath, projectId: projectId ?? '', inspectionId,
    });

    if (!window.open(route, '_blank')) navigate(route);
  };

  /**
   * Only the URL changes here. The effect above is what loads the file and puts the point
   * in the viewer slice — doing it in both places sent `setInspection` twice per click, and
   * each one closes the overlay.
   */
  const handlePoiClick = useCallback(
    (id: string, poi: Inspection.PointOfInterest) =>
      navigate(buildPath({ inspectionId: id, poiId: poi.id, stepIndex: poi.stepIndex ?? 0 })),
    [buildPath, navigate],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      {/* No back button: the breadcrumb in the header is how every other detail page in the
          app is left, and a second way up only competes with it. */}
      <Box sx={{ width: '100%' }}>
        {!isLoading && !inspection && (
          <Typography color="text.secondary">{t('Inspection_NotFound')}</Typography>
        )}

        {inspection && (
          <>
            {/* The header the other detail pages use: a tile, the name, what it is made of,
                and its actions on the right. */}
            {/* 2 rather than the 3 the other detail headers carry: the list below opens
                with its own padding, so the gap on screen lands where theirs does. */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <InspectionIconTile>
                  <ClipboardCheck size={26} />
                </InspectionIconTile>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>{inspection.info.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{inspection.info.id}</Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    <InspectionStatusSummary pois={pois} />

                    {snapshotCount > 0 && (
                      <Tooltip title={t('Common_Snapshots')}>
                        <SnapshotChip
                          icon={<Images size={14} />}
                          label={snapshotCount}
                          size="small"
                          color="primary"
                          variant="outlined"
                          clickable
                          disabled={snapshotsLoading}
                          onClick={() => void loadSnapshots().then(() => setGalleryOpen(true))}
                        />
                      </Tooltip>
                    )}

                    {inspection.author && (
                      <Tooltip title={t('Inspection_UploadedBy')}>
                        <InfoChip
                          icon={<User size={14} style={{ marginRight: '1px' }} />}
                          label={inspection.author.name}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}

                    <Tooltip title={t('Common_DateCreated')}>
                      <InfoChip
                        icon={<Calendar size={14} style={{ marginRight: '1px' }} />}
                        label={new Date(inspection.info.dateCreated).toAgoString()}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  </Box>
                </Box>

                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <Tooltip title={t('Inspection_OpenInViewer')}>
                    <IconButton onClick={openInViewer} sx={{ color: 'text.secondary' }}>
                      <OpenInBrowser />
                    </IconButton>
                  </Tooltip>

                  <InspectionReportDownload
                    inspection={inspection}
                    projectId={projectId ?? ''}
                    projectName={project?.projectInfo.name ?? '-'}
                  />
                </Stack>
              </Stack>
            </Paper>

            {/* Rows rather than one panel of rows: a point of interest is drawn as an
                inspection is on the list this page was reached from. */}
            {pagedPois.data.map((poi) => (
              <InspectionPointItem
                key={poi.id}
                poi={poi}
                inspectionId={inspection.info.id}
                onPoiClick={handlePoiClick}
              />
            ))}

            {pois.length === 0 && (
              <Paper variant="outlined">
                <EmptyPOIContainer>
                  <Typography color="text.secondary">{t('POI_NotFound')}</Typography>
                </EmptyPOIContainer>
              </Paper>
            )}

            {pagedPois.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={pagedPois.totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <ImageGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={snapshotImages}
        startIndex={0}
      />

      <InspectionDetailModal />
    </Box>
  );
};

export default InspectionDetailPage;
